import { useEffect, useState } from 'react';
import { useStore } from '@/redux/store';
import classNames from 'classnames';
import {
  ActionModal,
  Button,
  DropdownButton,
  EmptyState,
  LinkCards,
  PaginationBar,
  PaginationData,
  SpinningLoader,
  TextField,
  TextFieldInput,
} from 'br2s-ui-componentlibrary_r2_02';
import { AuthAPI } from '@/services/api/auth';
import { useNavigate } from 'react-router-dom';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import { PROFILE_TABLE_SORT_LABELS } from './profileSelection-constant';
import { LocalStorageService, useHTTPError } from 'br2s-ui-common-utils-02';
import {
  paginateMyEntities,
  getSortByLabel,
  toggleEntityChange,
  transformOrEntitiesToProfileRecords,
} from './profileSelection-utilities';
import { AccountAPI } from '@/services/api/accounts/accounts';
import { ProfileSwitchTableDataWithDropdown } from '@/redux/slices/profileSwitchSlice';
import useProfileSwitch from '@/utilities/hooks/useProfileSwitch/useProfileSwitch';

interface ManagedEntity {
  uen: string;
  uenName: string;
}

interface ProfileData {
  uen: string;
  uenName: string;
  managedEntities?: ManagedEntity[];
  todoCount?: number;
  hasHighPriority?: boolean;
  entityName?: string;
}

const ProfileSelectionTable = () => {
  const navigate = useNavigate();
  const { toggleLoading } = useStore();
  const {
    entities,
    selectedTabKey,
    resetProfileSwitchState,
    fetchProfilesIndividual,
    toggleTableLoad,
    setSearchValue: setEntitySearchValue,
    saveSingleProfileTypeRecord,
    setPagination,
  } = useProfileSwitch();
  const { handleHTTPError } = useHTTPError();

  const [sortBy, setSortBy] = useState(PROFILE_TABLE_SORT_LABELS[0].label);
  const [paginationData, setPaginationData] = useState({
    items: 0,
    itemsPerPage: 10,
    page: 1,
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<ProfileData | null>(
    null,
  );
  const [searchError, setSearchError] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const currentEntity = entities[
    selectedTabKey
  ] as ProfileSwitchTableDataWithDropdown;
  const tabPagination = currentEntity.pagination;

  const sortOptions = (
    selectedTabKey === 'myEntities'
      ? PROFILE_TABLE_SORT_LABELS.slice(0, 2)
      : PROFILE_TABLE_SORT_LABELS
  ).map((item) => ({
    label: item.label,
    onClick: () => handleSort(item.label),
    isSelected: sortBy === item.label,
  }));

  const sortingText = PROFILE_TABLE_SORT_LABELS.find(
    ({ label }) => label === sortBy,
  )?.paginationLabel;

  const tableItems =
    selectedTabKey === 'myEntities'
      ? paginateMyEntities(
          entities[selectedTabKey].data,
          tabPagination.pageNumber,
          tabPagination.pageSize,
          currentEntity.search,
          tabPagination.sortOrder,
        )
      : entities[selectedTabKey].data;

  // Sync redux pagination with internal state
  useEffect(() => {
    setPaginationData({
      items: tabPagination.items,
      page: tabPagination.pageNumber + 1,
      itemsPerPage: tabPagination.pageSize || 10,
    });

    if (selectedTabKey === 'myEntities' && currentEntity.search) {
      setPaginationData((data) => ({
        ...data,
        items: tableItems?.length || tabPagination.items,
      }));
    }

    setSortBy(getSortByLabel(tabPagination.sortBy, tabPagination.sortOrder));
    setSearchError('');
    setSearchValue(currentEntity.search);
  }, [currentEntity.search, tabPagination]);

  // Don't show switch profile prompt on first time login profile switch
  useEffect(() => {
    // istanbul ignore next
    if (selectedEntity && !localStorage.getItem('entityChanged')) {
      handleSwitchProfile();
    }
  }, [selectedEntity]);

  // istanbul ignore next
  const handlePaginationChange =
    (type: 'next' | 'back' | 'size' | 'page') => (value?: number) => {
      let page = 0;

      switch (type) {
        case 'next':
          page = tabPagination.pageNumber + 1;
          break;
        case 'back':
          page = tabPagination.pageNumber - 1;
          break;
        case 'page':
          page = value ? value - 1 : tabPagination.pageNumber;
          break;
      }

      const pagination = {
        page,
        itemsPerPage: type === 'size' && value ? value : tabPagination.pageSize,
      };

      if (selectedTabKey === 'myEntities') {
        return setPagination('myEntities', {
          pageNumber: pagination.page,
          pageSize: pagination.itemsPerPage,
        });
      }

      if (selectedTabKey === 'officialReceiver') {
        return getOrEntities(currentEntity.search, pagination);
      }

      fetchProfilesIndividual(
        currentEntity.selectedUen,
        selectedTabKey,
        pagination,
        currentEntity.search,
        currentEntity.pagination.sortOrder,
        currentEntity.pagination.sortBy,
      );
    };

  // istanbul ignore next
  const getOrEntities = async (
    search: string,
    { page, itemsPerPage }: Omit<PaginationData, 'items'>,
  ) => {
    toggleTableLoad(selectedTabKey, true);

    try {
      const uenResponse = await AccountAPI.getEntityInfo(
        'uen',
        search,
        page,
        itemsPerPage,
      );
      const uenResult = uenResponse.data.result?.entityProfiles;

      if (uenResult && uenResult.length) {
        saveSingleProfileTypeRecord(
          'officialReceiver',
          transformOrEntitiesToProfileRecords(uenResult ?? []),
          {
            items: uenResponse.data.result.totalCount,
            pageNumber: uenResponse.data.result.currentPage,
            pageSize: uenResponse.data.result.pageSize,
          },
        );
        toggleTableLoad(selectedTabKey, false);
        return;
      }

      const entityResponse = await AccountAPI.getEntityInfo(
        'entityName',
        search,
        page,
        itemsPerPage,
      );
      const entityResult = entityResponse.data.result?.entityProfiles;
      saveSingleProfileTypeRecord(
        'officialReceiver',
        transformOrEntitiesToProfileRecords(entityResult ?? []),
        {
          items: entityResponse.data.result.totalCount,
          pageNumber: entityResponse.data.result.currentPage,
          pageSize: entityResponse.data.result.pageSize,
        },
      );
    } catch (error) {
      // istanbul ignore next
      console.error(error);
      handleHTTPError(error, { errorType: 'inline' });
    } finally {
      toggleTableLoad(selectedTabKey, false);
    }
  };

  // istanbul ignore next
  const cardOnClick = async (item: { uenName: string; uen: string }) => {
    setSelectedEntity(item);

    if (localStorage.getItem('entityChanged')) {
      setIsConfirmOpen(true);
    }
  };

  // istanbul ignore next
  const getMyEntitiesSourceUen = () => {
    const myEntities = entities.myEntities.data || [];

    const { uen, uenName } = myEntities.reduce(
      (acc, { uen, uenName, managedEntities }) =>
        managedEntities?.some((entity) => entity.uen === selectedEntity?.uen)
          ? { uen: [...acc.uen, uen], uenName: [...acc.uenName, uenName] }
          : acc,
      { uen: [] as string[], uenName: [] as string[] },
    );

    return [uen, uenName];
  };

  // istanbul ignore next
  const getOfficialReceiverSourceUen = () => {
    const { uen, uenName, sourceUen, sourceUenName } =
      LocalStorageService.getAuthInfo(
        'uen',
        'uenName',
        'sourceUen',
        'sourceUenName',
      );

    const sourceUens = sourceUen ? [sourceUen] : [uen || ''];
    const sourceUenNames = sourceUenName ? [sourceUenName] : [uenName || ''];

    return [sourceUens, sourceUenNames];
  };

  // istanbul ignore next
  const getSourceUen = () => {
    if (selectedTabKey === 'myEntities') return getMyEntitiesSourceUen();
    if (selectedTabKey === 'officialReceiver')
      return getOfficialReceiverSourceUen();

    const uen = currentEntity.selectedUen ? [currentEntity.selectedUen] : [];
    const uenName = currentEntity.parentEntities?.find(
      (ent) => ent.uen === currentEntity.selectedUen,
    )?.uenName;

    return [uen, uenName ? [uenName] : []];
  };

  // istanbul ignore next
  const handleSwitchProfile = async () => {
    setIsConfirmOpen(false);
    toggleLoading(true);
    toggleEntityChange('false');
    try {
      const [sourceUens, sourceUenNames] = getSourceUen();
      const regenerateTokenResponse = await AuthAPI.regenerateToken({
        targetUenName: selectedEntity?.uenName as string,
        targetUen: selectedEntity?.uen as string,
        sourceUens,
        sourceUenNames,
        ...(selectedTabKey === 'myEntities'
          ? { myEntitiesSelected: true }
          : {}),
      });
      if (regenerateTokenResponse?.status === 200) {
        const sessionInfoResponse = await AuthAPI.getSessionInfo();

        if (sessionInfoResponse?.status === 200) {
          const { result } = sessionInfoResponse.data;
          const redirectUrl = LocalStorageService.getUrlPriorToLogin();
          localStorage.removeItem('exemptSwitchProfileDeny');
          LocalStorageService.setAuth(JSON.stringify(result));
          toggleEntityChange('true');
          navigate(redirectUrl ?? '/dashboard');
          LocalStorageService.clearUrlPriorToLogin();

          // Reset profile switch redux after navigate
          setTimeout(() => resetProfileSwitchState(), 0);
        }
      }
    } catch (error) {
      console.error(error);
      handleHTTPError(error, { errorType: 'inline' });
    } finally {
      toggleLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchValue && selectedTabKey === 'officialReceiver')
      return setSearchError('Enter a company name or UEN');

    if (searchValue && searchValue.length < 3)
      return setSearchError('Search key must contain at least 3 characters');

    setEntitySearchValue(selectedTabKey, searchValue);

    if (selectedTabKey === 'myEntities') {
      setPagination('myEntities', { pageNumber: 0 });
      return;
    }

    if (selectedTabKey === 'officialReceiver')
      return getOrEntities(searchValue, {
        itemsPerPage: tabPagination.pageSize,
        page: 0,
      });

    fetchProfilesIndividual(
      currentEntity.selectedUen,
      selectedTabKey,
      {
        page: 0,
        itemsPerPage: tabPagination.pageSize,
      },
      searchValue,
      currentEntity.pagination.sortOrder,
      currentEntity.pagination.sortBy,
    );
  };

  // istanbul ignore next
  const handleCancel = () => {
    setSelectedEntity(null);
    setIsConfirmOpen(false);
  };

  const handleSort = (label: string) => {
    let sortBy: 'lastUpdated' | 'alphabetical';
    let sortOrder: 'desc' | 'asc';

    // istanbul ignore next
    switch (label) {
      case 'Sort by: Alphabetical (A-Z)': {
        sortOrder = 'asc';
        sortBy = 'alphabetical';
        break;
      }
      case 'Sort by: Alphabetical (Z-A)': {
        sortOrder = 'desc';
        sortBy = 'alphabetical';
        break;
      }
      case 'Sort by: Earliest updated': {
        sortOrder = 'asc';
        sortBy = 'lastUpdated';
        break;
      }
      default: {
        sortOrder = 'desc';
        sortBy = 'lastUpdated';
      }
    }

    // istanbul ignore next
    if (selectedTabKey === 'myEntities') {
      return setPagination('myEntities', {
        sortBy,
        sortOrder,
      });
    }

    fetchProfilesIndividual(
      currentEntity.selectedUen,
      selectedTabKey,
      {
        page: tabPagination.pageNumber,
        itemsPerPage: tabPagination.pageSize,
      },
      currentEntity.search,
      sortOrder,
      sortBy,
    );
  };

  const renderTable = () => {
    if (currentEntity.loading) return <SpinningLoader />;

    if (!tableItems) return <></>;

    if (tableItems && !tableItems.length) {
      return (
        <div className="w-100">
          <EmptyState
            id="selection-profile-empty-state"
            title="No items found"
            icon={SystemIcon.GeneralEmpty}
          />
        </div>
      );
    }

    return (
      <>
        {selectedTabKey !== 'officialReceiver' && (
          <div className="sort-form w-100">
            <DropdownButton
              id="switch-profile-sort-button"
              type="quiet"
              label={getSortByLabel(
                currentEntity.pagination.sortBy,
                currentEntity.pagination.sortOrder,
              )}
              iconType="prepended"
              iconClassName={SystemIcon.ActionSort}
              dropdownItems={sortOptions}
              dropdownPosition="right"
              limitLabel={false}
            />
          </div>
        )}
        <div className="profile-item-list w-100 row">
          {tableItems.map(
            (item: {
              uenName: string;
              uen: string;
              todoCount?: number;
              hasHighPriority?: boolean;
            }) => (
              <div
                key={item.uen}
                className={classNames('link-card-selection col-6 mb-1 p-1', {
                  'remove-icon': !item.hasHighPriority,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  cardOnClick(item);
                }}
                id="cmp-link-cards-container"
              >
                <LinkCards
                  key={item.uen}
                  id="cmp-link-cards"
                  title={item.uenName}
                  metadataTop={[item.uen]}
                  cardSize="small"
                  href="#"
                  hasInlineIndicator={selectedTabKey !== 'officialReceiver'}
                  InlineStateIndicator={{
                    id: 'todo-count',
                    type: 'error',
                    label: `${item.todoCount}`,
                  }}
                />
              </div>
            ),
          )}
        </div>
        <div className="pagination-container w-100">
          <PaginationBar
            id="switch-profile-pagination"
            direction="vertical"
            data={paginationData}
            sortingText={sortingText}
            setData={setPaginationData}
            handleNextBtnClick={handlePaginationChange('next')}
            handlePrevBtnClick={handlePaginationChange('back')}
            handleItemChange={handlePaginationChange('size')}
            handlePageChange={handlePaginationChange('page')}
          />
        </div>
      </>
    );
  };

  return (
    <div className="switch-profile-table w-100">
      <div className="search-field w-100">
        <TextField
          id="switch-profile-search-field"
          state={searchError ? 'error' : 'default'}
        >
          <TextFieldInput
            id="switch-profile-input"
            name="switch-profile-input"
            placeholder={`Search by ${
              selectedTabKey === 'officialReceiver'
                ? 'entity name or exact UEN'
                : 'company name or UEN'
            }`}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setSearchError('');
            }}
            helperText={searchError}
          />
          <Button
            id="switch-profile-search-button"
            type="tertiary"
            label="Search"
            iconType="prepended"
            iconClassName={SystemIcon.ActionSearch}
            onClick={handleSearch}
          />
        </TextField>
      </div>
      {renderTable()}
      <ActionModal
        id="switch-profile-modal"
        isOpen={isConfirmOpen}
        title="Switching Profile"
        message="Save as draft or submit your forms before switching profile to avoid losing any changes."
        state="warning"
        actionButton1={{
          id: 'profile-cancel',
          type: 'secondary',
          label: 'Cancel',
          onClick: handleCancel,
        }}
        actionButton2={{
          id: 'profile-ok',
          type: 'primary',
          label: 'Proceed',
          onClick: handleSwitchProfile,
        }}
        onCloseClick={handleCancel}
      />
    </div>
  );
};

export default ProfileSelectionTable;
